import { motion } from "framer-motion";
import { useParams, Navigate, Link } from "react-router-dom";
import { ShieldCheck, Lock, EyeOff, UserCheck, Sparkles, Receipt, Phone, Smartphone, RefreshCw, KeyRound } from "lucide-react";

const privacyTrust = {
  badge: "Privacy-first by design",
  points: [
    { icon: Lock, title: "Data minimization", desc: "We collect information only when it's needed to run a feature, protect an account, or follow the law." },
    { icon: EyeOff, title: "No ad profiling of kids", desc: "We never use a child's location, video, or audio for behavioral or targeted advertising." },
    { icon: UserCheck, title: "Parents stay in control", desc: "Guardians can review, correct, delete, or withdraw consent for a child's information at any time." },
  ],
};

const termsTrust = {
  badge: "Clear, fair terms",
  points: [
    { icon: Sparkles, title: "Free VIP Early Access", desc: "Joining is free, never a purchase, and never requires a credit card — no obligation to buy later." },
    { icon: Receipt, title: "No hidden surprises", desc: "Pricing, taxes, and shipping estimates are always shown before you ever reach checkout." },
    { icon: Phone, title: "Safety comes first", desc: "Emergency features add an extra layer of connection but never replace calling 911 directly." },
  ],
};

const licenseTrust = {
  badge: "Simple software license",
  points: [
    { icon: Smartphone, title: "Yours to use", desc: "Install the Vsense app on compatible devices you own and connect it to your authorized glasses." },
    { icon: RefreshCw, title: "Automatic, secure updates", desc: "We keep the app improving with security patches and new features, installed when your device allows." },
    { icon: KeyRound, title: "Licensed, never sold", desc: "You get a personal, revocable license for family and caregiving use — with your privacy protected." },
  ],
};

const ease = [0.22, 1, 0.36, 1];

const POLICIES = {
  warranty: {
    title: "Warranty",
    updated: "June 2026",
    intro:
      "Every pair of Vsense glasses is covered by a limited warranty. We stand behind our craftsmanship and want you to wear your glasses with total confidence.",
    sections: [
      { heading: "What's covered", body: "Vsense provides a 1-year limited warranty against manufacturing defects in materials and workmanship under normal use. This includes issues with frames, hinges, electronics, camera, microphone, speakers and battery performance." },
      { heading: "What's not covered", body: "The warranty does not cover accidental damage, cosmetic wear (scratches, scuffs), loss or theft, unauthorized modifications, or damage from misuse, water beyond the IPX5 rating, or exposure to extreme conditions." },
      { heading: "Battery", body: "Rechargeable batteries are consumable and naturally lose capacity over time. We warrant the battery to retain at least 80% of its original capacity for one year from purchase." },
      { heading: "How to make a claim", body: "Contact support@vsense.com with your reservation or order number and a description of the issue. Our team will help diagnose the problem and arrange a repair or replacement where covered." },
      { heading: "Your legal rights", body: "This limited warranty is in addition to, and does not affect, any statutory consumer rights you may have under applicable law." },
    ],
  },
  shipping: {
    title: "VIP Early Access Shipping",
    updated: "June 2026",
    intro:
      "We're currently preparing for our first round of Vsense shipments, with VIP Early Access orders receiving priority. Our goal is to begin shipping in time for the 2026 holiday season.",
    highlight: "Targeting holiday 2026 delivery for VIP Early Access orders.",
    sections: [
      { heading: "Holiday 2026 timing", body: "We're working toward having VIP Early Access orders arrive before Christmas 2026. Delivery dates are estimates and may change depending on production schedules, order volume, product availability, carrier delays, and the customer's location." },
      { heading: "Order updates", body: "As your order moves closer to shipment, you'll receive email updates with the latest estimated timing. Tracking information will be sent once your order leaves our facility." },
      { heading: "VIP priority", body: "VIP Early Access members will receive the earliest available opportunity to place an order. Orders will be processed approximately in the order they are received, subject to product and color availability." },
      { heading: "Important notice", body: "Estimated shipping and delivery dates are provided for planning purposes and are not guaranteed. Joining the VIP Early Access list does not constitute a purchase or reserve a specific delivery date." },
    ],
  },
  returns: {
    title: "Returns",
    updated: "June 2026",
    intro:
      "We want you to love your Vsense glasses. If they're not right for you, returning them is simple and stress-free.",
    sections: [
      { heading: "30-day returns", body: "You may return your glasses within 30 days of delivery for a full refund of the product price, provided they're in original condition with all packaging and accessories." },
      { heading: "How to start a return", body: "Email support@vsense.com with your order number to receive a prepaid return label and instructions. Returns must be postmarked within the 30-day window." },
      { heading: "Refunds", body: "Once we receive and inspect your return, refunds are issued to your original payment method within 5–10 business days. You'll get an email as soon as your refund is processed." },
      { heading: "Prescription lenses", body: "Custom prescription lenses are made to order and are non-refundable unless the product is defective. The frame and standard components remain eligible for return." },
      { heading: "Exchanges", body: "Want a different model, color or size? Start a return and place a new order — reservation-holder pricing still applies to your exchange." },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    effective: "[Month Day, 2026]",
    trust: true,
    intro:
      "At Vsense, we understand that privacy is especially important when technology is used by children, older adults, and their caregivers. We designed our privacy practices around a simple principle: personal information should be collected only when it is needed to provide the service, protect an account, or comply with the law. This Privacy Policy explains how Vsense (\u201cVsense,\u201d \u201cwe,\u201d \u201cour,\u201d or \u201cus\u201d) collects, uses, shares, and protects information through our website, VIP Early Access program, mobile application, smart glasses, and related services.",
    sections: [
      {
        heading: "Information We Collect",
        content: [
          { type: "p", text: "The information we collect depends on the Vsense services and features you use." },
          { type: "sub", text: "Information provided by a parent, guardian, caregiver, or account holder" },
          { type: "p", text: "This may include:" },
          { type: "list", items: ["Name and email address", "Account login information", "Phone number", "Shipping and billing information", "Product preferences and selected colors", "Information submitted through customer support", "Guardian, caregiver, and approved-contact settings"] },
          { type: "p", text: "Payment information may be processed by our payment provider. Vsense does not intend to store complete payment-card information on its own systems." },
          { type: "sub", text: "Device and app information" },
          { type: "p", text: "When the Vsense app and glasses are available and activated, we may collect:" },
          { type: "list", items: ["Device identifiers and connection status", "Battery level and technical diagnostics", "App settings and feature preferences", "IP address, browser type, and operating-system information", "Security, crash, and performance information"] },
          { type: "sub", text: "Location, audio, and video" },
          { type: "p", text: "Certain Vsense features may require access to precise location, audio, or video." },
          { type: "p", text: "Depending on the settings selected by the parent, guardian, caregiver, or account holder, Vsense may process:" },
          { type: "list", items: ["Real-time and recent location information", "Video and audio transmitted through the glasses", "Two-way voice communications", "Navigation requests and emergency-feature activity"] },
          { type: "p", text: "We use this information to provide the features requested by the authorized account holder. We do not use a child's precise location, video, or audio for targeted advertising." },
          { type: "p", text: "The wearer should be informed when location, camera, or microphone features are active. Customers are responsible for using recording features lawfully and respecting the privacy of other people." },
        ],
      },
      {
        heading: "Children's Privacy",
        content: [
          { type: "p", text: "Vsense is intended to be set up and managed by a parent or legal guardian when the wearer is a child." },
          { type: "p", text: "We do not knowingly collect personal information directly from a child under 13 without first providing the required notice and obtaining verifiable consent from a parent or legal guardian." },
          { type: "p", text: "Parents and legal guardians may:" },
          { type: "list", items: ["Review the personal information associated with their child", "Request corrections", "Request deletion", "Withdraw consent for future collection", "Disable eligible features", "Request that an account be closed"] },
          { type: "p", text: "Withdrawing consent or deleting certain information may prevent location, communication, live-view, navigation, or emergency features from working." },
          { type: "p", text: "If we learn that personal information from a child under 13 was collected without appropriate parental consent, we will take reasonable steps to delete it." },
        ],
      },
      {
        heading: "How We Use Information",
        content: [
          { type: "p", text: "We may use information to:" },
          { type: "list", items: ["Create, secure, and manage accounts", "Operate the Vsense app and glasses", "Provide location, communication, navigation, and emergency features", "Process orders and send shipping updates", "Manage the VIP Early Access program", "Respond to customer-support requests", "Diagnose technical issues and improve reliability", "Prevent fraud, misuse, and unauthorized access", "Send product communications when permitted", "Meet legal, safety, and regulatory obligations"] },
          { type: "p", text: "We will not use children's personal information for purposes that are unrelated to providing and protecting the Vsense service without providing additional notice and obtaining any required consent." },
        ],
      },
      {
        heading: "When Information May Be Shared",
        content: [
          { type: "p", text: "We may share limited information with service providers that help us operate Vsense, such as companies providing:" },
          { type: "list", items: ["Cloud hosting and secure data storage", "Email and customer communications", "Payment processing", "Shipping and order fulfillment", "Mapping and location services", "Customer support", "Security monitoring and technical diagnostics"] },
          { type: "p", text: "These providers may access information only as needed to perform services for Vsense and must handle it according to their agreements with us and applicable law." },
          { type: "p", text: "We may also disclose information when reasonably necessary to comply with the law, respond to valid legal requests, protect a person from serious harm, investigate fraud, or protect the rights and security of Vsense and its users." },
          { type: "p", text: "We do not sell children's personal information. We do not share children's precise location, video, or audio for behavioral or targeted advertising." },
        ],
      },
      {
        heading: "Website Analytics and Advertising",
        content: [
          { type: "p", text: "Our public website may use cookies or similar technologies to understand site performance, measure advertising, remember preferences, and improve the visitor experience." },
          { type: "p", text: "These website technologies should not be used within a child's device profile to build an advertising profile based on the child's location, video, audio, or app activity." },
          { type: "p", text: "Where required, visitors may manage eligible cookie and advertising choices through our cookie settings or browser controls." },
        ],
      },
      {
        heading: "Data Retention",
        content: [
          { type: "p", text: "We retain personal information only for as long as reasonably necessary to provide the requested service, maintain security, comply with legal obligations, resolve disputes, and enforce our agreements." },
          { type: "p", text: "Location history, video, audio, and child-related information will not be retained indefinitely. Retention periods may depend on the feature, account settings, safety requirements, and applicable law." },
          { type: "p", text: "When information is no longer needed, we will take reasonable steps to delete it or remove identifying details." },
        ],
      },
      {
        heading: "Security",
        content: [
          { type: "p", text: "We use reasonable administrative, technical, and organizational safeguards designed to protect personal information. These may include encryption, access controls, account authentication, monitoring, and limits on employee and service-provider access." },
          { type: "p", text: "No connected product or internet transmission can be guaranteed to be completely secure. Parents, guardians, and caregivers should use strong passwords, protect their account credentials, and review the people authorized to access the wearer's information." },
        ],
      },
      {
        heading: "Your Privacy Choices",
        content: [
          { type: "p", text: "Depending on where you live, you may have the right to:" },
          { type: "list", items: ["Request access to personal information", "Request correction or deletion", "Receive a copy of certain information", "Withdraw consent", "Limit certain uses or disclosures", "Opt out of eligible advertising-related sharing", "Appeal a decision concerning a privacy request"] },
          { type: "p", text: "To submit a request, contact us at [privacy@yourdomain.com]. We may need to verify your identity, authority, or parental relationship before completing the request." },
        ],
      },
      {
        heading: "Emergency Features",
        content: [
          { type: "p", text: "Vsense emergency features are intended to provide an additional communication option. They are not a replacement for emergency services, professional supervision, medical care, or responsible caregiving." },
          { type: "p", text: "Location, network availability, battery level, device condition, and third-party service availability may affect how these features operate." },
        ],
      },
      {
        heading: "International Users",
        content: [
          { type: "p", text: "If Vsense becomes available outside the United States, additional privacy notices or rights may apply based on the user's location. We will provide any additional disclosures or consent choices required before offering applicable services in those regions." },
        ],
      },
      {
        heading: "Changes to This Policy",
        content: [
          { type: "p", text: "We may update this Privacy Policy as Vsense develops, laws change, or new features become available." },
          { type: "p", text: "If an update materially changes how children's personal information, precise location, video, or audio is handled, we will provide appropriate notice and obtain additional consent when required." },
          { type: "p", text: "The effective date at the top of this page shows when the policy was last updated." },
        ],
      },
      {
        heading: "Contact Us",
        content: [
          { type: "p", text: "Questions or privacy requests may be sent to:" },
          { type: "list", items: ["Vsense", "Email: [privacy@yourdomain.com]", "Mailing address: [Business mailing address]", "Website: [Website URL]"] },
          { type: "p", text: "For requests involving a child's account, please contact us using the email address connected to the parent or guardian account." },
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    effective: "September 21, 2026",
    trust: true,
    intro:
      "Welcome to Vsense. These Terms of Service (\u201cTerms\u201d) explain the rules for using the Vsense website, VIP Early Access program, mobile application, smart glasses, and related products and services. By accessing or using a Vsense service, you agree to these Terms and our Privacy Policy. If you do not agree, please do not use the service.",
    sections: [
      {
        heading: "1. About Vsense",
        content: [
          { type: "p", text: "Vsense develops smart glasses and connected services intended to help families and caregivers stay connected." },
          { type: "p", text: "Depending on the product, account settings, network availability, and location, features may include:" },
          { type: "list", items: ["Live video and audio", "Location information", "Two-way voice communication", "Voice-activated calling", "Navigation assistance", "Emergency-calling assistance", "Guardian and caregiver settings"] },
          { type: "p", text: "Features described on our website may still be under development and may change before the final product is released." },
        ],
      },
      {
        heading: "2. Parents, Guardians, and Caregivers",
        content: [
          { type: "p", text: "A parent or legal guardian must create and manage the account when the wearer is a child who cannot legally consent for themselves." },
          { type: "p", text: "By creating or managing an account for another person, you confirm that:" },
          { type: "list", items: ["You are authorized to act for that person.", "The information you provide is accurate.", "You will explain the product's features to the wearer in an age-appropriate manner.", "You will manage authorized contacts and account permissions responsibly.", "You will follow applicable privacy, recording, school, and safety rules."] },
          { type: "p", text: "Children under 13 may not create their own independent Vsense accounts. Any collection of personal information from a child under 13 is subject to parental notice and consent requirements." },
        ],
      },
      {
        heading: "3. VIP Early Access",
        content: [
          { type: "p", text: "Joining the Vsense VIP Early Access list is free and does not require a credit card." },
          { type: "p", text: "Joining the list:" },
          { type: "list", items: ["Is not a purchase or preorder.", "Does not guarantee that a product will be available.", "Does not reserve a particular color, size, or model.", "Does not guarantee a specific price, shipping date, or delivery date.", "Does not require you to purchase a product later."] },
          { type: "p", text: "VIP members may receive product updates, an early opportunity to place an order, a private checkout link, and a promotional launch offer." },
          { type: "p", text: "Promotional offers may be subject to eligibility requirements, expiration dates, product availability, and single-use restrictions. Offers may not be combined unless Vsense states otherwise." },
        ],
      },
      {
        heading: "4. Orders and Payments",
        content: [
          { type: "p", text: "When ordering becomes available, product details, pricing, estimated shipping information, taxes, and applicable fees will be shown before checkout." },
          { type: "p", text: "Submitting an order does not guarantee acceptance. We may decline or cancel an order because of product availability, suspected fraud, pricing errors, payment issues, shipping restrictions, or other reasonable business concerns." },
          { type: "p", text: "If we cancel an order after payment has been collected, we will issue an appropriate refund to the original payment method." },
          { type: "p", text: "Payment processing may be handled by a third-party provider under that provider's terms and privacy practices." },
        ],
      },
      {
        heading: "5. Shipping Estimates",
        content: [
          { type: "p", text: "Shipping and delivery dates are estimates unless Vsense clearly states otherwise in writing." },
          { type: "p", text: "We are currently targeting the 2026 holiday season for initial VIP Early Access shipments. Our goal is to have eligible VIP orders arrive before Christmas 2026, but holiday delivery is not guaranteed." },
          { type: "p", text: "Production schedules, order volume, product availability, carrier delays, weather, customs, and delivery location may affect timing." },
          { type: "p", text: "Tracking information will be provided when available." },
        ],
      },
      {
        heading: "6. Product Availability and Changes",
        content: [
          { type: "p", text: "Vsense products and services may evolve during development. We may modify:" },
          { type: "list", items: ["Product appearance and materials", "Available colors and sizes", "Software features", "App interfaces", "Battery performance", "Compatibility requirements", "Pricing and promotional offers", "Expected release and shipping schedules"] },
          { type: "p", text: "We will make reasonable efforts to describe material product details accurately before accepting payment." },
          { type: "p", text: "Website images, prototypes, renderings, demonstrations, and early-access materials may show preproduction versions. Final products may differ in reasonable ways." },
        ],
      },
      {
        heading: "7. Responsible Use",
        content: [
          { type: "p", text: "You agree to use Vsense lawfully, safely, and respectfully." },
          { type: "p", text: "You may not use Vsense to:" },
          { type: "list", items: ["Secretly record someone where recording is prohibited", "Stalk, harass, threaten, intimidate, or monitor someone unlawfully", "Invade another person's privacy", "Record in restrooms, changing areas, medical settings, or other sensitive locations without lawful authorization", "Violate school, workplace, venue, or property rules", "Commit fraud or impersonate another person", "Interfere with the product's security or operation", "Access another account without permission", "Attempt to reverse-engineer or misuse the software", "Use the product for unlawful surveillance"] },
          { type: "p", text: "Camera, microphone, and location features must be used in accordance with applicable laws and the rights of other people." },
        ],
      },
      {
        heading: "8. School and Venue Rules",
        content: [
          { type: "p", text: "Vsense does not guarantee that its glasses will be permitted inside every school, classroom, testing environment, workplace, healthcare facility, venue, or private property." },
          { type: "p", text: "Parents, guardians, caregivers, and wearers are responsible for understanding and following applicable rules. Vsense is not responsible if a school or other organization restricts or prohibits use of the glasses." },
        ],
      },
      {
        heading: "9. Emergency Features",
        content: [
          { type: "p", text: "Vsense emergency features are intended to provide an additional communication option. They are not a replacement for calling 911 from a reliable telephone, contacting local emergency services, direct adult supervision, or following an established safety plan." },
          { type: "p", text: "Emergency and communication features may be affected by:" },
          { type: "list", items: ["Battery level", "Wireless or cellular coverage", "Internet availability", "Phone pairing or device compatibility", "Location accuracy", "Hardware condition", "Software settings", "Third-party service availability", "Local emergency-service capabilities"] },
          { type: "p", text: "If immediate help is needed, contact 911 or the appropriate local emergency service directly whenever possible." },
          { type: "p", text: "Vsense does not guarantee that an emergency call, alert, location, video, audio, or message will always connect, transmit, or be received." },
        ],
      },
      {
        heading: "10. Location Information",
        content: [
          { type: "p", text: "Location information can be delayed, incomplete, or inaccurate because of signal conditions, indoor environments, device settings, network availability, or other technical limitations." },
          { type: "p", text: "Location features should not be the only method used to determine whether a child, older adult, or other wearer is safe." },
        ],
      },
      {
        heading: "11. Health and Medical Disclaimer",
        content: [
          { type: "p", text: "Vsense is not a medical device unless a specific product is expressly identified and legally authorized as one." },
          { type: "p", text: "Vsense does not diagnose, treat, cure, monitor, or prevent dementia, memory loss, developmental conditions, or any other medical condition." },
          { type: "p", text: "The product is not a replacement for professional healthcare, responsible caregiving, direct supervision, prescription eyewear, or an individualized safety plan." },
          { type: "p", text: "Speak with an appropriate professional if you have questions about whether the glasses are suitable for a particular wearer." },
        ],
      },
      {
        heading: "12. Account Security",
        content: [
          { type: "p", text: "You are responsible for:" },
          { type: "list", items: ["Protecting your password and login credentials", "Keeping account information current", "Reviewing authorized guardians and caregivers", "Removing access when it is no longer appropriate", "Informing us promptly about suspected unauthorized access"] },
          { type: "p", text: "Please contact [support@yourdomain.com] immediately if you believe an account or device has been compromised." },
        ],
      },
      {
        heading: "13. Privacy",
        content: [
          { type: "p", text: "Our collection and use of personal information are described in the Vsense Privacy Policy." },
          { type: "p", text: "Certain features may require permission to access location, camera, microphone, contacts, Bluetooth, notifications, or other device functions. Disabling a required permission may prevent the related feature from working." },
          { type: "p", text: "Parents and guardians may have additional rights concerning information associated with a child." },
        ],
      },
      {
        heading: "14. Software and Updates",
        content: [
          { type: "p", text: "Vsense may release software, firmware, security, or app updates. Some updates may be installed automatically when permitted by the device or account settings." },
          { type: "p", text: "Updates may be necessary to maintain compatibility, security, reliability, or feature availability. Refusing an essential update may prevent some features from working correctly." },
          { type: "p", text: "You receive a limited, personal, revocable, nonexclusive, and nontransferable right to use Vsense software with an authorized Vsense product. The software is licensed, not sold." },
        ],
      },
      {
        heading: "15. Third-Party Services",
        content: [
          { type: "p", text: "Some features may depend on third-party services, including mapping, communications, payment processing, cloud hosting, mobile networks, shipping, and emergency-service providers." },
          { type: "p", text: "Vsense does not control every third-party service and cannot guarantee its continuous availability. Your use of certain third-party services may also be governed by their terms and privacy policies." },
        ],
      },
      {
        heading: "16. Ownership",
        content: [
          { type: "p", text: "Vsense and its licensors own the website, software, product designs, graphics, logos, branding, written content, and other protected materials associated with the service." },
          { type: "p", text: "You may not copy, reproduce, sell, distribute, modify, or create unauthorized derivative works from Vsense materials without written permission." },
        ],
      },
      {
        heading: "17. Feedback",
        content: [
          { type: "p", text: "If you voluntarily provide suggestions or product feedback, you permit Vsense to use that feedback to evaluate and improve its products and services without an obligation to compensate you." },
          { type: "p", text: "We will handle personal information included with feedback according to our Privacy Policy." },
        ],
      },
      {
        heading: "18. Returns and Product Warranties",
        content: [
          { type: "p", text: "Returns, exchanges, cancellations, and refunds will be governed by the policies presented when purchases become available." },
          { type: "p", text: "Any written product warranty will be provided separately and will explain:" },
          { type: "list", items: ["The warranty period", "What is covered", "What is not covered", "How to request warranty service", "Any legally permitted limitations"] },
          { type: "p", text: "Nothing in these Terms removes consumer rights that cannot legally be waived." },
        ],
      },
      {
        heading: "19. Service Availability",
        content: [
          { type: "p", text: "We work to provide reliable services, but no connected product can operate without interruption." },
          { type: "p", text: "To the extent permitted by law, Vsense does not guarantee that every feature will always be available, accurate, uninterrupted, secure, or error-free. Maintenance, updates, outages, network conditions, and events outside our reasonable control may temporarily affect service." },
        ],
      },
      {
        heading: "20. Limitation of Liability",
        content: [
          { type: "p", text: "To the fullest extent permitted by applicable law, Vsense will not be liable for indirect, incidental, special, consequential, or punitive damages arising from use of\u2014or inability to use\u2014the services." },
          { type: "p", text: "Nothing in these Terms excludes or limits liability that cannot legally be excluded, including rights provided by applicable consumer-protection law." },
          { type: "p", text: "Because laws differ by location, some limitations may not apply to you." },
        ],
      },
      {
        heading: "21. Suspension or Termination",
        content: [
          { type: "p", text: "We may limit, suspend, or terminate access when reasonably necessary to:" },
          { type: "list", items: ["Protect a wearer or another person", "Investigate suspected fraud or unlawful activity", "Address unauthorized surveillance or privacy violations", "Protect account or service security", "Comply with a legal obligation", "Respond to a serious violation of these Terms"] },
          { type: "p", text: "When appropriate, we will provide notice and an opportunity to contact us." },
          { type: "p", text: "You may stop using Vsense or request account closure at any time, subject to applicable retention and legal requirements." },
        ],
      },
      {
        heading: "22. Changes to These Terms",
        content: [
          { type: "p", text: "We may update these Terms as our products develop or legal requirements change." },
          { type: "p", text: "If a change materially affects your rights or use of the service, we will provide reasonable notice. The effective date at the top identifies the latest version." },
          { type: "p", text: "Continued use after updated Terms become effective means you accept the revised Terms, to the extent permitted by law." },
        ],
      },
      {
        heading: "23. Governing Law",
        content: [
          { type: "p", text: "These Terms are governed by the laws of [State], without regard to conflict-of-law principles." },
          { type: "p", text: "Any dispute that is not resolved informally will be handled in the appropriate state or federal courts located in [County, State], unless applicable law gives you the right to bring a claim elsewhere." },
          { type: "p", text: "Before filing a formal claim, you may contact us at [legal@yourdomain.com] so we can attempt to resolve the concern." },
        ],
      },
      {
        heading: "24. Contact Us",
        content: [
          { type: "p", text: "Questions about these Terms may be sent to:" },
          { type: "list", items: ["Vsense", "Email: [legal@yourdomain.com]", "Customer support: [support@yourdomain.com]", "Mailing address: [Business mailing address]", "Website: [Website URL]"] },
        ],
      },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    updated: "June 2026",
    intro:
      "This policy explains how Vsense uses cookies and similar technologies on our website.",
    sections: [
      { heading: "What are cookies", body: "Cookies are small text files stored on your device that help websites function and remember your preferences. Similar technologies include local storage and pixels." },
      { heading: "How we use them", body: "We use essential cookies to keep the site working (like remembering your cart and session), analytics cookies to understand how the site is used, and preference cookies to remember your choices." },
      { heading: "Essential cookies", body: "These are required for core functionality such as keeping your reservation cart and staying signed in. The site cannot function properly without them." },
      { heading: "Analytics cookies", body: "These help us measure traffic and improve the experience. The data is aggregated and does not identify you personally." },
      { heading: "Managing cookies", body: "You can control or delete cookies through your browser settings. Blocking essential cookies may affect how the site works." },
    ],
  },
  licenses: {
    title: "End User License Agreement",
    effective: "September 21, 2026",
    trust: true,
    intro:
      "This End User License Agreement (\u201cLicense Agreement\u201d) governs your use of the Vsense mobile application, smart-glasses software, firmware, updates, and related digital features (\u201cSoftware\u201d). This License Agreement works together with the Vsense Terms of Service and Privacy Policy. By downloading, installing, accessing, or using the Software, you agree to this License Agreement. If you do not agree, do not install or use the Software.",
    sections: [
      {
        heading: "1. License Grant",
        content: [
          { type: "p", text: "Subject to this License Agreement, Vsense grants you a limited, personal, revocable, nonexclusive, nontransferable, and nonsublicensable license to use the Software with an authorized Vsense product and compatible device." },
          { type: "p", text: "This license permits you to use the Software only for its intended personal, family, caregiving, and household purposes." },
          { type: "p", text: "The Software is licensed to you and is not sold." },
        ],
      },
      {
        heading: "2. Parents, Guardians, and Caregivers",
        content: [
          { type: "p", text: "When the wearer is a child, a parent or legal guardian must install, configure, and manage the Software." },
          { type: "p", text: "By managing the Software for another person, you confirm that:" },
          { type: "list", items: ["You are authorized to act on that person's behalf.", "You will manage account permissions responsibly.", "You will explain relevant features to the wearer.", "You will comply with applicable privacy and recording laws.", "You will review the people authorized to access the account.", "You will follow applicable school, workplace, venue, and property rules."] },
          { type: "p", text: "Children under 13 may not independently accept this License Agreement or create an independent Vsense account." },
        ],
      },
      {
        heading: "3. Permitted Use",
        content: [
          { type: "p", text: "You may:" },
          { type: "list", items: ["Install the Vsense app on compatible devices you own or control.", "Connect the Software to authorized Vsense glasses.", "Configure guardian, caregiver, communication, and safety settings.", "Use available features for lawful personal and caregiving purposes.", "Install updates provided or authorized by Vsense.", "Allow authorized household or caregiver accounts to use eligible features."] },
          { type: "p", text: "Your access remains subject to device compatibility, feature availability, account permissions, and the Vsense Terms of Service." },
        ],
      },
      {
        heading: "4. License Restrictions",
        content: [
          { type: "p", text: "You may not:" },
          { type: "list", items: ["Copy or distribute the Software except as permitted by law", "Sell, rent, lease, sublicense, or commercially exploit the Software", "Reverse-engineer, decompile, or attempt to discover its source code, except where applicable law expressly permits it", "Modify or create unauthorized derivative works", "Remove copyright, trademark, ownership, or security notices", "Bypass account, authentication, parental-control, or security measures", "Install the Software on unauthorized or counterfeit hardware", "Introduce malware or harmful code", "Interfere with the Software's operation or connected services", "Use the Software for unlawful surveillance, stalking, harassment, or privacy violations", "Access another person's account or information without permission", "Use automated methods to scrape, copy, or extract data from the Software", "Misrepresent that an unauthorized product or service is approved by Vsense"] },
        ],
      },
      {
        heading: "5. Camera, Microphone, and Location Features",
        content: [
          { type: "p", text: "Certain Software features may use the glasses' camera, microphone, location capabilities, Bluetooth connection, or paired mobile device." },
          { type: "p", text: "You are responsible for:" },
          { type: "list", items: ["Obtaining any required permission or consent", "Informing the wearer about active features", "Respecting the privacy of people nearby", "Using recording and location features lawfully", "Disabling features when their use would be inappropriate", "Following school, workplace, venue, and property rules"] },
          { type: "p", text: "The Software may provide indicators or notices when certain features are active. You may not intentionally bypass or conceal those indicators." },
        ],
      },
      {
        heading: "6. Software Updates",
        content: [
          { type: "p", text: "Vsense may provide updates, patches, security improvements, firmware changes, or feature modifications." },
          { type: "p", text: "Some updates may install automatically when allowed by your device settings. Updates may:" },
          { type: "list", items: ["Add or remove features", "Correct errors", "Improve compatibility or security", "Change the app interface", "Address legal or safety requirements", "Affect older devices or operating systems"] },
          { type: "p", text: "An update may be required for continued access to certain features. Refusing or failing to install an essential update may prevent the Software or glasses from operating correctly." },
        ],
      },
      {
        heading: "7. Ownership",
        content: [
          { type: "p", text: "Vsense and its licensors retain all rights, title, and interest in the Software, including its code, design, interfaces, graphics, documentation, trademarks, and other protected materials." },
          { type: "p", text: "This License Agreement grants permission to use the Software. It does not transfer ownership of the Software or any Vsense intellectual property." },
          { type: "p", text: "All rights not expressly granted are reserved." },
        ],
      },
      {
        heading: "8. Third-Party Software and Services",
        content: [
          { type: "p", text: "The Software may include, connect with, or depend on third-party components and services, including:" },
          { type: "list", items: ["Mobile operating systems", "Mapping and navigation providers", "Cloud-hosting services", "Communication providers", "Payment processors", "Security and diagnostic tools", "Emergency-service providers"] },
          { type: "p", text: "Third-party components may be subject to separate terms, licenses, or privacy policies." },
          { type: "p", text: "Open-source components, when included, remain governed by their applicable open-source licenses. If an open-source license grants rights that conflict with this License Agreement, the open-source license controls for that component." },
          { type: "p", text: "Vsense does not control the continuous availability of every third-party service." },
        ],
      },
      {
        heading: "9. App Store Terms",
        content: [
          { type: "p", text: "If you download the Vsense app through Apple's App Store or Google Play, your use of the app is also subject to the applicable store's rules." },
          { type: "p", text: "The app-store provider is not responsible for:" },
          { type: "list", items: ["Maintaining or supporting the Vsense app", "Providing Vsense customer service", "Addressing product claims involving Vsense", "Operating Vsense hardware or connected features"] },
          { type: "p", text: "To the extent required by the applicable app store, the app-store provider may be treated as a third-party beneficiary of this License Agreement and may enforce the applicable provisions." },
        ],
      },
      {
        heading: "10. Privacy and Data",
        content: [
          { type: "p", text: "Use of the Software may involve processing account, device, location, audio, video, and technical information." },
          { type: "p", text: "The Vsense Privacy Policy explains:" },
          { type: "list", items: ["What information may be collected", "Why it is collected", "How it may be used", "When it may be shared", "How parents, guardians, and users may exercise applicable privacy rights"] },
          { type: "p", text: "Disabling permissions for location, camera, microphone, Bluetooth, notifications, or other required functions may prevent related features from working." },
        ],
      },
      {
        heading: "11. Emergency and Safety Limitations",
        content: [
          { type: "p", text: "The Software and glasses are intended to provide additional communication and connection tools. They are not a replacement for:" },
          { type: "list", items: ["Calling 911 from a reliable telephone", "Direct adult supervision", "Professional caregiving", "Medical care", "School safety procedures", "An individualized emergency plan"] },
          { type: "p", text: "Emergency, communication, location, camera, and audio features may be affected by battery level, wireless coverage, internet availability, phone pairing, device compatibility, location accuracy, hardware condition, service outages, or third-party systems." },
          { type: "p", text: "Vsense does not guarantee that an emergency call, alert, location, video, audio, or message will always connect, transmit, or be received." },
        ],
      },
      {
        heading: "12. No Medical Use",
        content: [
          { type: "p", text: "The Software is not intended to diagnose, treat, cure, monitor, or prevent dementia, memory loss, developmental conditions, or any other medical condition." },
          { type: "p", text: "It is not a substitute for medical advice, professional care, direct supervision, prescription eyewear, or an individualized care plan." },
        ],
      },
      {
        heading: "13. Support",
        content: [
          { type: "p", text: "Questions about installation, account access, compatibility, or Software operation may be sent to:" },
          { type: "list", items: ["[support@yourdomain.com]"] },
          { type: "p", text: "Available support methods and response times may change as Vsense products and services develop." },
        ],
      },
      {
        heading: "14. Warranty and Service Disclaimers",
        content: [
          { type: "p", text: "Product warranties, returns, and remedies for Vsense hardware will be described in the warranty and return policies provided when purchases become available." },
          { type: "p", text: "The Software may occasionally experience interruptions, errors, delays, or compatibility problems." },
          { type: "p", text: "To the extent permitted by law, Vsense does not guarantee that the Software will always be uninterrupted, completely secure, error-free, or compatible with every device, network, or third-party service." },
          { type: "p", text: "Nothing in this License Agreement limits consumer rights or warranties that cannot legally be excluded." },
        ],
      },
      {
        heading: "15. Limitation of Liability",
        content: [
          { type: "p", text: "The limitation-of-liability provisions in the Vsense Terms of Service apply to the Software and are incorporated into this License Agreement." },
          { type: "p", text: "Nothing in this License Agreement excludes liability that cannot legally be excluded or limits rights provided by applicable consumer-protection law." },
        ],
      },
      {
        heading: "16. Suspension and Termination",
        content: [
          { type: "p", text: "This license remains effective until terminated." },
          { type: "p", text: "You may terminate it by deleting the Software, disconnecting your account, and stopping use of the Software." },
          { type: "p", text: "Vsense may suspend or terminate the license if you:" },
          { type: "list", items: ["Seriously or repeatedly violate this License Agreement", "Use the Software unlawfully", "Compromise the security of the Software or another account", "Use the Software for unauthorized surveillance", "Attempt to bypass security or privacy protections", "Use unauthorized or counterfeit hardware"] },
          { type: "p", text: "When this license ends, you must stop using the Software. Provisions concerning ownership, restrictions, liability, and other terms that reasonably should continue will survive termination." },
          { type: "p", text: "Account information will be handled according to the Vsense Privacy Policy and applicable law." },
        ],
      },
      {
        heading: "17. Export and Sanctions Compliance",
        content: [
          { type: "p", text: "You may not download, export, re-export, or use the Software in violation of applicable export-control, trade, or sanctions laws." },
          { type: "p", text: "You confirm that you are not prohibited from receiving or using the Software under applicable law." },
        ],
      },
      {
        heading: "18. Changes to This License Agreement",
        content: [
          { type: "p", text: "Vsense may update this License Agreement as products develop, features change, or legal requirements evolve." },
          { type: "p", text: "If an update materially affects your rights, we will provide reasonable notice. Continued use after the updated agreement becomes effective means you accept the revised terms, to the extent permitted by law." },
        ],
      },
      {
        heading: "19. Governing Law",
        content: [
          { type: "p", text: "This License Agreement is governed by the laws of [State], without regard to conflict-of-law principles." },
          { type: "p", text: "Disputes relating to this License Agreement will be handled according to the dispute provisions in the Vsense Terms of Service." },
        ],
      },
      {
        heading: "20. Contact Us",
        content: [
          { type: "p", text: "Questions about this License Agreement may be sent to:" },
          { type: "list", items: ["Vsense", "Legal email: [legal@yourdomain.com]", "Customer support: [support@yourdomain.com]", "Mailing address: [Business mailing address]", "Website: [Website URL]"] },
        ],
      },
    ],
  },
};

export default function Policy({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const policy = POLICIES[slug];

  if (!policy) return <Navigate to="/" replace />;

  const trust = slug === "privacy" ? privacyTrust : slug === "terms" ? termsTrust : slug === "licenses" ? licenseTrust : null;

  return (
    <div data-testid={`policy-page-${slug}`} className="bg-white">
      <section className="pt-28 md:pt-36 pb-12 md:pb-16 bg-[#F5F5F7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
          >
            {policy.trust && trust && (
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-semibold tracking-wide text-emerald-800 uppercase">{trust.badge}</span>
              </div>
            )}
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">
              {policy.effective && policy.updated
                ? `Legal · Effective ${policy.effective} · Last updated ${policy.updated}`
                : policy.effective
                ? `Legal · Effective ${policy.effective}`
                : `Legal · Last updated ${policy.updated}`}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-zinc-900 leading-[1.05]">
              {policy.title}
            </h1>
            <p className="mt-5 text-base md:text-lg text-zinc-500 leading-relaxed">{policy.intro}</p>
            {policy.trust && trust && (
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {trust.points.map((t) => (
                  <div key={t.title} className="rounded-2xl border border-zinc-200 bg-white p-5">
                    <div className="w-9 h-9 rounded-xl bg-zinc-900 flex items-center justify-center">
                      <t.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="mt-3 text-sm font-semibold text-zinc-900">{t.title}</div>
                    <div className="mt-1 text-[13px] text-zinc-500 leading-relaxed">{t.desc}</div>
                  </div>
                ))}
              </div>
            )}
            {policy.highlight && (
              <div
                data-testid="policy-highlight"
                className="mt-6 inline-flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4"
              >
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                <span className="text-sm md:text-base font-semibold text-amber-900 leading-relaxed">
                  {policy.highlight}
                </span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="space-y-12">
            {policy.sections.map((s, i) => (
              <div key={i} data-testid={`policy-section-${i}`}>
                <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-zinc-900">
                  {s.heading}
                </h2>
                {s.body && <p className="mt-3 text-base text-zinc-600 leading-relaxed">{s.body}</p>}
                {s.content &&
                  s.content.map((node, j) => {
                    if (node.type === "sub")
                      return (
                        <h3 key={j} className="mt-6 text-base font-semibold text-zinc-900">
                          {node.text}
                        </h3>
                      );
                    if (node.type === "list")
                      return (
                        <ul key={j} className="mt-3 space-y-1.5 pl-1">
                          {node.items.map((it, k) => (
                            <li key={k} className="flex gap-2.5 text-base text-zinc-600 leading-relaxed">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-300" />
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    return (
                      <p key={j} className="mt-3 text-base text-zinc-600 leading-relaxed">
                        {node.text}
                      </p>
                    );
                  })}
              </div>
            ))}
          </div>

          <div className="mt-16 pt-8 border-t border-zinc-200">
            <p className="text-sm text-zinc-500">
              Questions about this policy?{" "}
              <Link to="/contact" className="font-medium text-zinc-900 underline-offset-4 hover:underline">
                Get in touch
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
